<script lang="ts">
	//Hooks
	import { onMount } from 'svelte';
	//Formatters
	import { format } from '../../../../services/i18n';
	//Requests
	import { localReq, postReq } from '../../../../services/http';
	//Helpers
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	import type { Device } from 'src/types/Device';
	//Stores
		import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Design system components
	import { Button, toast, Toggle, Text, TimeWheel } from '@Shelly/shelly-design-system';
	import {
		convertSeconds,
		convertToSeconds,
		formatTwoDigits,
		formatTimeLabel,
		getTimeWheelTexts,
		getTimeWheelMsStep,
		getTimeWheelToggleText,
	} from '../../../../helpers/timer_helpers';

	export let device: Device;

	let values = { on: 0, off: 0 },
		subsettings: Array<'on' | 'off'> = ['on', 'off'],
		id = real_id(device.id);

	let settingsPerChannel;
	let min = 0,
		max = 65535,
		localIp = get_local_ip(id),
		loading = { on: false, off: false };

	let lastCustomValues = { on: 60, off: 60 };

	let selectedHour = { on: '00', off: '00' };
	let selectedMinute = { on: '00', off: '00' };
	let selectedSecond = { on: '00', off: '00' };
	let selectedMs = { auto_on_delay: undefined, auto_off_delay: undefined };
	let maxTimes = convertSeconds(max);

	$: infoText = `${$format('timer_info_description', {
		values: {
			min: min,
			max: formatTimeLabel(
				maxTimes.hours,
				maxTimes.minutes,
				maxTimes.seconds,
				maxTimes.milliseconds,
			),
		},
	})}`;

	$: errors = {
		on:
			convertToSeconds(
				selectedHour['on'],
				selectedMinute['on'],
				selectedSecond['on'],
				selectedMs['on'],
			) > max
				? infoText
				: '',
		off:
			convertToSeconds(
				selectedHour['off'],
				selectedMinute['off'],
				selectedSecond['off'],
				selectedMs['off'],
			) > max
				? infoText
				: '',
	};

	onMount(() => {
		extractSettings();
	});

	const getTimeInSeconds = (type: 'on' | 'off') => {
		return convertToSeconds(
			selectedHour[type],
			selectedMinute[type],
			selectedSecond[type],
			selectedMs[type],
		);
	};

	const updateTimeValues = (type: 'on' | 'off') => {
		const { hours, minutes, seconds, milliseconds } = convertSeconds(values[type] || 0);
		selectedHour[type] = formatTwoDigits(hours);
		selectedMinute[type] = formatTwoDigits(minutes);
		selectedSecond[type] = formatTwoDigits(seconds);
		selectedMs[type] = milliseconds ? formatTwoDigits(milliseconds) : undefined;
	};

	const extractSettings = () => {
		const deviceSettings = readDeviceSettings(id);
		if (device.category === 'emeter') {
			if (deviceSettings?.['relays']?.[0]) {
				settingsPerChannel = deviceSettings['relays'][0];
				subsettings.forEach((option) => {
					values[option] = settingsPerChannel['auto_' + option];
					if (values[option] > 0) lastCustomValues[option] = values[option];
				});
			}
		} else {
			if (deviceSettings?.[device.category + 's']?.[device.channel]) {
				settingsPerChannel = deviceSettings[device.category + 's'][device.channel];
				subsettings.forEach((option) => {
					values[option] = settingsPerChannel['auto_' + option];
					if (values[option] > 0) lastCustomValues[option] = values[option];
				});
			}
		}

		updateTimeValues('on');
		updateTimeValues('off');

		return settingsPerChannel;
	};

	const setAuto = async (setting: 'on' | 'off') => {
		let res;
		loading[setting] = true;

		const secconds = getTimeInSeconds(setting);
		let value = secconds;
		if (localIp) {
			res = await localReq(
				device,
				`${localIp}/settings/${device.category === 'emeter' ? 'relay' : device.category}/${device.channel}`,
				'post',
				{
					[`auto_${setting === 'on' ? 'on' : 'off'}`]: value,
				},
			);
		} else {
			if (device.category !== 'emeter') {
				res = await postReq(`/device/${device.category}/settings/auto_turn`, {
					id: id,
					channel: device.channel,
					timeout: value,
					state: setting,
				});
			} else {
				res = await postReq(`/device/relay/settings/auto_turn`, {
					id: id,
					channel: 0,
					timeout: value,
					state: setting,
				});
			}
		}
		if (res.isok) {
			const formattedTime = formatTimeLabel(
				selectedHour[setting],
				selectedMinute[setting],
				selectedSecond[setting],
				selectedMs[setting],
			);
			const toastKey = `auto_${setting}_set_toast`;
			toast.open({
				type: 'success',
				title:
					value > 0
						? `${$format(toastKey)} ${formattedTime}.`
						: $format('auto_turn_successfully_disable', {
								values: { auto_turn_state: $format(`auto_${setting}`) },
							}),
			});
		} else {
			toast.open({ type: 'danger', title: $format('timer_err_toast') });
		}
		loading[setting] = false;
	};

	const handleToggler = async (type: 'on' | 'off') => {
		if (loading[type]) return;

		if (values[type] > 0) {
			lastCustomValues[type] = values[type];
			values[type] = 0;
		} else {
			values[type] = lastCustomValues[type] > 0 ? lastCustomValues[type] : 60;
		}
		updateTimeValues(type);
		await setAuto(type);
	};
</script>

{#each subsettings as setting, index}
	{#if index > 0}
		<hr class="mt-l mb-l" />
	{/if}

	<Toggle
		size="m"
		checked={values[setting] > 0}
		disabled={loading[setting]}
		on:click={() => handleToggler(setting)}
		id="toggle-timer-g1-{setting}"
		aria-label={$format('auto_' + setting)}
	>
		<Text element="span" styleType="controls-small" class="text-white">
			{$format('auto_' + setting)}
		</Text>
	</Toggle>

	<Text styleType="tiny" class="text-white" style="margin-top: 2px">
		{$format(`auto_${setting}_toggle_info`, {
			default:
				setting === 'on'
					? 'Turns the device on automatically after the selected time'
					: 'Turns the device off automatically after the selected time',
		})}
	</Text>

	{#if values[setting] > 0}
		<Text styleType="controls-small" class="mt-s-plus mb-s">
			{$format(`auto_${setting}_time_label`, {
				default: setting === 'on' ? 'Turn the device on after' : 'Turn the device off after',
			})}
			<Text element="span" styleType="body-small" class="text-primary text-bold">
				{formatTimeLabel(
					selectedHour[setting],
					selectedMinute[setting],
					selectedSecond[setting],
					selectedMs[setting],
				)}
			</Text>
		</Text>

		<TimeWheel
			id="timewheel-qyxd9g1-{setting}"
			a11yProps={{
				inputAriaLabel: $format('label_time_wheel_input', { default: 'Time wheel input' }),
				buttonAriaLabel: $format('label_time_wheel_button', {
					default: 'Time wheel button',
				}),
				secondsColumnAriaLabel: $format('label_time_wheel_seconds_column', {
					default: 'Time wheel seconds column',
				}),
				secondPrefixAriaLabel: $format('label_time_wheel_second_prefix', {
					default: 'Time wheel second prefix',
				}),
				msColumnAriaLabel: $format('label_time_wheel_ms_column', {
					default: 'Time wheel ms column',
				}),
				msPrefixAriaLabel: $format('label_time_wheel_ms_prefix', {
					default: 'Time wheel ms prefix',
				}),
				hoursColumnAriaLabel: $format('label_time_wheel_hours_column', {
					default: 'Time wheel hours column',
				}),
				hourPrefixAriaLabel: $format('label_time_wheel_hour_prefix', {
					default: 'Time wheel hour prefix',
				}),
				minutesColumnAriaLabel: $format('label_time_wheel_minutes_column', {
					default: 'Time wheel minutes column',
				}),
				minutePrefixAriaLabel: $format('label_time_wheel_minute_prefix', {
					default: 'Time wheel minute prefix',
				}),
			}}
			class="mb-s"
			bind:selectedHour={selectedHour[setting]}
			bind:selectedMinute={selectedMinute[setting]}
			bind:selectedSecond={selectedSecond[setting]}
			bind:selectedMs={selectedMs[setting]}
			maxHours={maxTimes.hours}
			msToggleText={getTimeWheelToggleText()}
			info={infoText}
			error={errors[`${setting}`]}
			texts={getTimeWheelTexts()}
			msStep={getTimeWheelMsStep(device.type)}
		/>

		<Button
			size="m"
			variant={'primary'}
			width="full"
			loading={loading[setting]}
			disabled={loading[setting] || errors[`${setting}`]}
			on:click={() => setAuto(setting)}
		>
			{$format('button_save')}
		</Button>
	{/if}
{/each}
